import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import i18n from 'i18next';
import PropTypes from 'prop-types';
import moment from 'moment';
import { AutoSizer, List, CellMeasurerCache } from 'react-virtualized';
import 'react-perfect-scrollbar/dist/css/styles.css';

import Checkbox from '../form/checkbox/checkbox';
import { DroppablePayloadTypes } from '../folders/folder-list';
import { withClassNavigate } from '../../hocs/withClassNavigate';
import { getCredentials } from '../../selectors/application';
import { getSelectedFolder } from '../../selectors/folders';
import { getSelectedFolderMessageList } from '../../selectors/messages';
import { selectMessage, setError, selectFolder } from '../../actions/application';
import { setSelected, addMessageClassification, setIsFullMessageContent } from '../../actions/messages';
import { appendFilteredMessages } from '../../actions/application';
import { setMessageFlagged, searchMailsElastic } from '../../services/message';
import { enrichItemsWithReadStatuses } from '../../services/read-status';
import { editNewMessage } from '../../services/application';
import { readMessage, readMessageRaw } from '../../services/message-read';
import { resolveMessageFolder, findRemainingSelectedMessage } from '../../utils/utils';
import FavoriteIcon from '../../assets/icons/favorite.svg?react';
import TagIcon from '../../assets/icons/tag.svg?react';
import ClipIcon from '../../assets/icons/clip.svg?react';
import './message-list.scss';

function parseFrom(from) {
  const firstFrom = from && from.length > 0 ? from[0] : '';
  const formattedFrom = firstFrom.match(/^"(.*)"$/);
  return formattedFrom !== null ? formattedFrom[1] : firstFrom;
}

function parseTo(recipients) {
  let to = '';

  if (recipients && recipients.length && recipients.length > 0) {
    const rec = recipients[0];
    to = rec.address || '';
  }

  return to;
}

function _dragImage(messages, x, y) {
  const imageNode = document.createElement('span');
  imageNode.draggable = true;
  imageNode.style.opacity = '1';
  imageNode.style.position = 'absolute';
  imageNode.style.top = `${Math.max(0, y)}px`;
  imageNode.style.left = `${Math.max(0, x)}px`;
  imageNode.style.pointerEvents = 'none';
  imageNode.style.padding = '6px';
  imageNode.style.backgroundColor = 'white';
  imageNode.innerHTML = i18n.t('messageList.moveEmails', {
    emailCount: messages.length
  });
  return imageNode;
}

class MessageList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loadingFilteredMessages: false
    };
    this.cache = new CellMeasurerCache({
      fixedWidth: true,
      defaultHeight: 54,
      minHeight: 54
    });
  }

  renderEmptyState(message) {
    return (
      <Fragment>
        <ul className="AutoSizerWrapper">
          <div className="mt-2 mb-2 message-name-folder-container text-center">
            <span className="inter-regular-14 text-color-grey-600">
              {message}
            </span>
          </div>
        </ul>
      </Fragment>
    );
  }

  renderFolderMessageList() {
    const { selectedFolder } = this.props;
    if (this.props.messages.length === 0) {
      return this.renderEmptyState(
        i18n.t('messageList.noMessages')
      );
    }
    return <Fragment>
      <ul className="AutoSizerWrapper">
        <div className="mt-2 mb-2 message-name-folder-container">
          <span className="inter-semibold-14 text-color-blue-lefebvre">
            {selectedFolder.name}
          </span>
          <div className="message-list-divider" />
        </div>
        <AutoSizer>
          {({ height, width }) => (
            <List
              className="List"
              height={height}
              width={width}
              rowRenderer={this.renderItem.bind(this)}
              rowCount={this.props.messages.length}
              rowHeight={this.cache.rowHeight}
            />
          )}
        </AutoSizer>
      </ul>
    </Fragment>;
  }

  renderFilteredMessageList() {
    if (this.props.filteredMessages.length === 0) {
      return this.renderEmptyState(
        i18n.t('messageList.noResults')
      );
    }
    if (this.props.filtersApplied && Object.keys(this.props.filtersApplied).length > 0) {
      // Handler for infinite scroll
      const handleRowsRendered = async ({ stopIndex }) => {
        // If user scrolls within 10 items of the end, and there are more to load
        if (
          stopIndex >= this.props.filteredMessages.length - 10 &&
          this.props.filteredMessages.length < this.props.searchTotalHits &&
          this.props.searchFrom < this.props.searchTotalHits &&
          !this.state.loadingFilteredMessages
        ) {
          this.setState({ loadingFilteredMessages: true });
          // Dispatch action to load more (implement fetchMoreFilteredMessages in your props)
          const { userId, account } = this.props.lexon;
          const { lexonToken } = this.props.all.lexon;
          const result = await searchMailsElastic(
            this.props.dispatch,
            userId,
            account,
            this.props.filtersApplied,
            { from: this.props.searchFrom + this.props.searchSize, size: this.props.searchSize, lexonToken }
          );
          // Before appending, fetch read statuses for this new chunk
          const newItems = result.data.items || [];
          const enrichedItems =
            await enrichItemsWithReadStatuses(this.props.dispatch, newItems, userId, account, lexonToken);
          this.props.appendFilteredMessages(
            enrichedItems,
            this.props.searchFrom + this.props.searchSize,
            this.props.searchSize
          );
          this.setState({ loadingFilteredMessages: false });
        }
      };

      return (
        <Fragment>
          <ul className="AutoSizerWrapper">
            <div className="mt-2 mb-2 message-name-folder-container">
              <span className="inter-semibold-14 text-color-blue-lefebvre">
                {i18n.t("message-header.all-results")}
              </span>
              <div className="message-list-divider" />
            </div>
            <AutoSizer>
              {({ height, width }) => (
                <List
                  className="List"
                  height={height}
                  width={width}
                  rowRenderer={this.renderItem.bind(this)}
                  rowCount={this.props.filteredMessages.length}
                  rowHeight={this.cache.rowHeight}
                  overscanRowCount={10}
                  scrollToIndex={this.props.searchFrom}
                  onRowsRendered={handleRowsRendered}
                />
              )}
            </AutoSizer>
          </ul>
        </Fragment>
      );
    }
    return null;
  }

  render() {
    const { showPreview } = this.props;
    return (
      <div
        className={`${!showPreview ?
        'messageList-h' : 'split-messageList-h'} 
        messageList
        ${this.props.className}`}>
        {this.props.filtersApplied && Object.keys(this.props.filtersApplied).length > 0 ? (
          this.renderFilteredMessageList()
        ) : (
          this.renderFolderMessageList()
        )}
      </div>
    );
  }

  componentDidMount() {
    window.addEventListener('SendMessagesClassifications', this.sendMessagesClassifications);
    if (this.props.selectedMessages.filter(x => x.uid).length === 0) {
      this.props.messageClicked(null);
    }
  }

   openedLexonIdEmail = null;

   componentDidUpdate(prevProps) {
    const { messages, lexon } = this.props;
    if (messages.length !== prevProps.messages.length || (lexon.idEmail && messages.length > 0)) {
      const normalizeId = value => {
        if (value === null) {
          return '';
        }
        if (typeof value === 'undefined') {
          return '';
        }
        return String(value).trim();
      };
      const targetId = normalizeId(lexon && lexon.idEmail);
      if (!targetId) {
        return;
      }
      // Open the Lexon deep-link message only once per idEmail. Without this guard every list
      // change (e.g. deleting other messages) re-opens it and marks it as read.
      if (this.openedLexonIdEmail === targetId) {
        return;
      }
      const foundMessage = messages.find(m => normalizeId(m && m.messageId) === targetId);
      if (foundMessage) {
        this.openedLexonIdEmail = targetId;
        this.messageClicked(foundMessage);
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener(
      'SendMessagesClassifications',
      this.sendMessagesClassifications
    );
  }

  sendMessagesClassifications = event => {
    const findSelectedMessage = this.props.selectedMessagesClassification.find(
      x => x.id === event.detail.id
    );
    const newSelectedMessage = {
      ...findSelectedMessage,
      idActuation: event.detail.idActuation
    };
    this.props.addMessageClassification([{...newSelectedMessage}]);
  }

  fromMStoFormattedDate(ms) {
    // If a string with timezone (e.g., 2025-03-28T18:37:19+01:00), respect its zone then convert to local
    if (typeof ms === 'string') {
      return moment.parseZone(ms).local().format('DD/MM/YYYY · HH:mm');
    }
    // For Date or timestamp, render as local time
    return moment(ms).local().format('DD/MM/YYYY · HH:mm');
  }

  getExplodedItemsList = () => {
    const explodedItems = this.props.all?.folders?.explodedItems;
    if (!explodedItems) {
      return [];
    }
    return Array.isArray(explodedItems)
      ? explodedItems
      : Object.values(explodedItems);
  }

  getMessageFolder = message => resolveMessageFolder(
    message,
    this.props.selectedFolder,
    this.props.filtersApplied,
    this.props.all
  )

  renderItem({ key, index, style }) {
    const useFilteredMessages = this.props.filtersApplied && Object.keys(this.props.filtersApplied).length > 0;
    const rawMessage = useFilteredMessages ? this.props.filteredMessages[index] : this.props.messages[index];

    if (!rawMessage) {
      return null;
    }

    const message = useFilteredMessages ? {
      messageId: rawMessage.metadata.messageId,
      subject: rawMessage.metadata.subject,
      receivedDate: rawMessage.metadata.date,
      from: rawMessage.metadata.addressFrom.map(f => f.name || f.email),
      recipients: (rawMessage.metadata.addressTo || []).map(t => ({ address: t.name || t.email })),
      hasAttachments: rawMessage.metadata.attachments && rawMessage.metadata.attachments.length > 0,
      seen: !!rawMessage.metadata.seen,
      flagged: false, // Assuming default value, as it's not in filteredMessages
      important: rawMessage.metadata.isImportant,
      size: rawMessage.metadata.sizeMail,
      uid: rawMessage.metadata.idExternal, // Or rawMessage.metadata.id
      deleted: false, // Assuming default value
      // Include possible folder identifiers from Elastic metadata for per-message folder resolution
      folderId: rawMessage.metadata.folderId || rawMessage.metadata.idFolder,
      folderFullName:
        rawMessage.metadata.folderFullName ||
        rawMessage.metadata.folder ||
        rawMessage.metadata.folderPath ||
        rawMessage.metadata.path
    } : rawMessage;

    return this.renderRow({ key, style, message });
  }

  renderRow({ key, style, message }) {
    const folder = this.getMessageFolder(message);
    const useFilteredMessages = this.props.filtersApplied && Object.keys(this.props.filtersApplied).length > 0;
    const selectedId = message.uid === this.props.selectedMessage.uid;
    const { showPreview } = this.props;
    const folderId = folder && folder.folderId;
    const compositeId = `${message.subject}||-||${message.receivedDate}`;
    const selectedItem = this.props.selectedMessages.find(
      msg => msg.folderId === folderId && (
        (msg.uid && message.uid && msg.uid === message.uid) ||
        (!msg.uid && (msg.messageId === (message.messageId !== null ? message.messageId : compositeId)))
      )
    );
    const selected = !!selectedItem;

    let attachments = message.hasAttachments;
    if (
      this.props.downloadedMessages[message.messageId] &&
      this.props.downloadedMessages[message.messageId].attachments
    ) {
      attachments =
        this.props.downloadedMessages[message.messageId].attachments.length > 0;
    }
    const isDrafts =
      folder?.attributes?.find(
        att => att && att.toUpperCase() === '\\DRAFTS'
      ) ||
      folder.name?.toUpperCase() === 'DRAFTS' ||
      folder.name?.toUpperCase() === 'BORRADORES' ||
      folder.name?.toUpperCase() === 'BORRADOR';

    return (
      <li
        style={style}
        key={key}
        draggable={true}
        onDragStart={event => this.onDragStart(event, folder, message)}
        className={`
          align-items-start
          d-flex
          ${selectedId &&
            (this.props.selectedMessages.filter(x => x.uid).length === 1
              || this.props.selectedMessages.filter(x => x.uid).length === 0
            ) ? 'selected-message-list' : ''}
          ${message.deleted ? 'deleted' : ''}
          message-row-item
          ${showPreview ? 'message-row-item-preview' : ''}
          `
        }
      >
        <div className={`d-flex ml-2 mr-2 justify-content-center align-items-center message-row-item-checkbox`}>
          <Checkbox
            id={message.messageId}
            onChange={event => this.selectMessage(event, message)}
            checked={selected}
          />
        </div>

        <div
          className={`message-row-container-details
          ${message.seen ? 'font-weight-normal' : 'font-weight-semi-bold'}`}
          onClick={() => {
            this.messageClicked(message);
          }}
          draggable={true}
          onDragStart={event => this.onDragStart(event, folder, message)}
        >
          <div className={`w-100 ${!showPreview ? "message-row-item-details" : "message-row-item-details-split"}`}>
            <div className="message-row-item-text-container">
              <div className={`message-row-item-subject`}>
                <p className={`text-color-grey-800 ${message.seen ? 'inter-regular-16' : 'inter-semibold-16'}`}>
                  {(folder && folder.type && folder.type.attribute !== '\\Sent' && !isDrafts) && parseFrom(message.from)}
                  {(folder && folder.type && folder.type.attribute === '\\Sent' && !isDrafts) && parseTo(message.recipients)}
                  {(folder && folder.type && isDrafts) && parseTo(message.recipients)}
                </p>
              </div>

              <div className={
                `message-row-item-subject
                ${message.seen ? 'inter-medium-12 text-color-grey-800' : 
                    'inter-semibold-12 text-color-blue-lefebvre'}
              `}>
                <p>
                  {message.subject}
                </p>
              </div>
            </div>
            <div className="d-flex align-items-end flex-column justify-content-center">
              <div>{this.fromMStoFormattedDate(message.receivedDate)}</div>
              {(!showPreview && useFilteredMessages) &&
                <div>
                  <span>{folder.name}</span>
                </div>
              }
              <div className="d-flex align-items-center justify-content-end" style={{ minHeight: 18 }}>
              {!useFilteredMessages && (
                <div
                  className={`
                  message-row-item-indicator
                  message-row-item-favorite
                  ${message.flagged ? "show-indicator" : "hide-indicator"}`
                  }
                  title={i18n.t("message-header.favorite")}>
                  <span
                    id={`favorite-${message.uid}`}
                    className="ml-2"
                    onClick={event => {
                      event.stopPropagation();
                      this.props.toggleMessageFlaggedForFolder(this.props.credentials, folder, message);
                    }}
                  >
                    <FavoriteIcon />
                  </span>
                </div>
              )}
                {message.important && (
                  <div title={i18n.t("message-header.important")}>
                    <span id={`important-${message.uid}`} className="ml-2">
                      <TagIcon />
                    </span>
                  </div>
                )}
                {attachments && (
                  <div title={i18n.t("message-header.attachment")}>
                    <span id={`attachment-${message.uid}`} className="ml-2">
                      <ClipIcon />
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="message-item-divider"/>
        </div>
      </li>
    );
  }

  onDragStart(event, fromFolder, message) {
    event.stopPropagation();
    const payload = { type: DroppablePayloadTypes.MESSAGES, fromFolder };
    if (this.props.selectedMessages.length > 0) {
      // Prevent dragging single messages when there is a selection and message is not part of the selection
      const isInSelection = this.props.selectedMessages.find(
        s => s.folderId === fromFolder.folderId && (
          (s.uid && s.uid === message.uid) ||
          (!s.uid && s.messageId === message.messageId)
        )
      );
      if (!isInSelection) {
        event.preventDefault();
        return;
      }
      const messages = this.props.selectedMessages.filter(
        s => s.folderId === fromFolder.folderId && (
          (s.uid && s.uid) || (!s.uid && s.messageId)
        )
      );
      if (event.dataTransfer.setDragImage) {
        const image = _dragImage(
          messages,
          event.pageX,
          event.pageY
        );
        const appendedImage = document.body.appendChild(image);
        setTimeout(() => document.body.removeChild(appendedImage));
        event.dataTransfer.setDragImage(image, -8, -16);
      }
      payload.messages = messages;
    } else {
      payload.messages = [message];
    }
    event.dataTransfer.setData('application/json', JSON.stringify(payload));
  }

  /**
   * Select/unselects the message for which the checkbox is changed.
   *
   * If the shift key is pressed, and it's a select operation, a range of messages will be selected. The range will be
   * the one consisting in the last selected message and the current message in any direction.
   *
   * @param event
   * @param message
   */
  selectMessage(event, message) {
    event.stopPropagation();
    const checked = event.target.checked;
    const folderOfMessage = this.getMessageFolder(message);
    if (
      checked &&
      event.nativeEvent &&
      event.nativeEvent.shiftKey &&
      this.props.selectedMessages.length > 0
    ) {
      // Range selection
      const messagesToSelect = [];
      const lastSelectedMessageUid =
        this.props.selectedMessages[this.props.selectedMessages.length - 1];
      let selecting = false;
      this.props.messages.forEach(m => {
        if (
          m.messageId === message.messageId ||
          m.messageId === lastSelectedMessageUid.messageId
        ) {
          selecting = !selecting;
          messagesToSelect.push(m);
        } else if (selecting) {
          messagesToSelect.push(m);
        }
      });

      for (let i = 0; i < messagesToSelect.length; i++) {
        this.props.messageSelected(
          [messagesToSelect[i]],
          checked,
          this.getMessageFolder(messagesToSelect[i]).fullName,
          this.getMessageFolder(messagesToSelect[i]).folderId,
          messagesToSelect[i].uid
        );
      }

      if (checked === true) {
        messagesToSelect.forEach(msg => {
          let msgId = msg.messageId;
          if (msgId) {
            msgId = msgId.replace("\t", "").replace("\n", "");
          } else {
            msgId = `${msg.subject}||-||${msg.receivedDate}`;
          }

          window.dispatchEvent(new CustomEvent('LoadingMessage', {detail: msgId}));
        });
      }

      const prs = [];

      for (let i = 0; i < messagesToSelect.length; i++) {
        const currentMessage = messagesToSelect[i];
        if (checked === true) {
          prs.push(
            readMessageRaw(
              this.props.dispatch,
              this.props.credentials,
              null,
              this.getMessageFolder(currentMessage),
              currentMessage
            ).then(response => {
              // PEC-8747: un rechazo del proveedor al leer el mensaje no cierra la sesión
              return response;
            })
          );
        } else {
          let msgId2 = currentMessage.messageId;
          if (msgId2) {
            msgId2 = msgId2.replace("\t", "").replace("\n", "");
          } else {
            msgId2 = `${currentMessage.subject}||-||${currentMessage.receivedDate}`;
          }

          const msgFolder = this.getMessageFolder(currentMessage);
          window.dispatchEvent(
            new CustomEvent('Checkclick', {
              detail: {
                uid: message.uid,
                id: msgId2,
                extMessageId: msgId2,
                subject: currentMessage.subject,
                sentDateTime: currentMessage.receivedDate,
                chkselected: checked,
                account: this.props.all.login.formValues.user,
                folder: msgFolder && msgFolder.fullName,
                provider: 'IMAP',
                raw: null
              }
            })
          );
        }
      }

      if (checked === true) {
        Promise.all(prs).then(msgs => {
          for (let i = 0; i < msgs.length; i++) {
            const msg = msgs[i];

            let msgId3 = msg.message.messageId;
            if (msgId3) {
              msgId3 = msgId3.replace("\t", "").replace("\n", "");
            } else {
              msgId3 = `${msg.message.subject}||-||${msg.message.receivedDate}`;
            }

            const msgFolder3 = this.getMessageFolder(msg.message);
            window.dispatchEvent(
              new CustomEvent('Checkclick', {
                detail: {
                  uid: msg.message.uid,
                  id: msgId3,
                  extMessageId: msgId3,
                  subject: msg.message.subject,
                  sentDateTime: msg.message.receivedDate,
                  chkselected: checked,
                  account: this.props.all.login.formValues.user,
                  folder: msgFolder3 && msgFolder3.fullName,
                  provider: 'IMAP',
                  raw: msg.raw
                }
              })
            );

            msg.raw = null;
          }
          window.dispatchEvent(new CustomEvent('LoadedMessage'));
        });
      }
    } else {
      // Single selection
      this.props.messageSelected(
        [message],
        checked,
        folderOfMessage && folderOfMessage.fullName,
        folderOfMessage && folderOfMessage.folderId,
        message.uid
      );

      if (checked === true) {
        this.props.messageClickedWithFolderNoSeen(folderOfMessage, message);
        let msgId4 = message.messageId;
        if (msgId4) {
          msgId4 = msgId4.replace("\t", "").replace("\n", "");
        } else {
          msgId4 = `${message.subject}||-||${message.receivedDate}`;
        }

        window.dispatchEvent(new CustomEvent('LoadingMessage', {detail: msgId4}));

        readMessageRaw(
          this.props.dispatch,
          this.props.credentials,
          null,
          folderOfMessage,
          message
        ).then(response => {
          // PEC-8747: un rechazo del proveedor al leer el mensaje no cierra la sesión ni recarga
          if (!response || response.unAuthorized) {
            window.dispatchEvent(new CustomEvent('LoadedMessage'));
            return;
          }
          // Send message to connectors
          window.dispatchEvent(
            new CustomEvent('Checkclick', {
              detail: {
                uid: message.uid,
                id: msgId4,
                extMessageId: msgId4,
                subject: message.subject,
                sentDateTime: message.receivedDate,
                chkselected: checked,
                account: this.props.all.login.formValues.user,
                folder: folderOfMessage && folderOfMessage.fullName,
                provider: 'IMAP',
                raw: response.raw
              }
            })
          );
          response = null;
          window.dispatchEvent(new CustomEvent('LoadedMessage'));
        });
      } else {
        this.props.messageClickedWithFolderBound(folderOfMessage, null);
        const remainingMessage = findRemainingSelectedMessage(this.props.selectedMessages, message);
        if (remainingMessage) {
          // PEC-8348: in multi-folder search results the remaining message may live in a
          // different folder than the one just unchecked. Resolve its own folder, otherwise
          // readMessage hits /folders/{wrong folder}/messages/{uid} and gets a 404.
          this.props.messageClickedWithFolderNoSeen(
            this.getMessageFolder(remainingMessage),
            remainingMessage
          );
        }

        let msgId5 = message.messageId;
        if (msgId5) {
          msgId5 = msgId5.replace("\t", "").replace("\n", "");
        } else {
          msgId5 = `${message.subject}||-||${message.receivedDate}`;
        }

        window.dispatchEvent(
          new CustomEvent('Checkclick', {
            detail: {
              uid: message.uid,
              id: msgId5,
              extMessageId: msgId5,
              subject: message.subject,
              sentDateTime: message.receivedDate,
              chkselected: checked,
              account: this.props.all.login.formValues.user,
              folder: folderOfMessage && folderOfMessage.fullName,
              provider: 'IMAP',
              raw: null
            }
          })
        );
      }
    }
  }

  selectedMessageLexon(message, checked) {
    const folderOfMessage = this.getMessageFolder(message);
    this.props.messageSelected(
      [message],
      checked,
      folderOfMessage && folderOfMessage.fullName,
      folderOfMessage && folderOfMessage.folderId,
      message.uid
    );

    if (checked === true) {
      let msgId4 = message.messageId;
      if (msgId4) {
        msgId4 = msgId4.replace("\t", "").replace("\n", "");
      } else {
        msgId4 = `${message.subject}||-||${message.receivedDate}`;
      }

      window.dispatchEvent(new CustomEvent('LoadingMessage', {detail: msgId4}));

      readMessageRaw(
        this.props.dispatch,
        this.props.credentials,
        null,
        folderOfMessage,
        message
      ).then(response => {
        // PEC-8747: un rechazo del proveedor al leer el mensaje no cierra la sesión ni recarga
        if (!response || response.unAuthorized) {
          window.dispatchEvent(new CustomEvent('LoadedMessage'));
          return;
        }
        // Send message to connectors
        window.dispatchEvent(
          new CustomEvent('Checkclick', {
            detail: {
              uid: message.uid,
              id: msgId4,
              extMessageId: msgId4,
              subject: message.subject,
              sentDateTime: message.receivedDate,
              chkselected: checked,
              account: this.props.all.login.formValues.user,
              folder: folderOfMessage && folderOfMessage.fullName,
              provider: 'IMAP',
              raw: response.raw
            }
          })
        );
        response = null;
        window.dispatchEvent(new CustomEvent('LoadedMessage'));
      });
    } else {
      let msgId5 = message.messageId;
      if (msgId5) {
        msgId5 = msgId5.replace("\t", "").replace("\n", "");
      } else {
        msgId5 = `${message.subject}||-||${message.receivedDate}`;
      }

      window.dispatchEvent(
        new CustomEvent('Checkclick', {
          detail: {
            uid: message.uid,
            id: msgId5,
            extMessageId: msgId5,
            subject: message.subject,
            sentDateTime: message.receivedDate,
            chkselected: checked,
            account: this.props.all.login.formValues.user,
            folder: folderOfMessage && folderOfMessage.fullName,
            provider: 'IMAP',
            raw: null
          }
        })
      );
    }
  }

  messageClicked(message) {
    const folderOfMessage = this.getMessageFolder(message);
    if (
     folderOfMessage && folderOfMessage.attributes && folderOfMessage.attributes.find(
        att => att && att.toUpperCase() === '\\DRAFTS'
      ) ||
      (folderOfMessage && folderOfMessage.name && (
        folderOfMessage.name.toUpperCase() === 'DRAFTS' ||
        folderOfMessage.name.toUpperCase() === 'BORRADORES' ||
        folderOfMessage.name.toUpperCase() === 'BORRADOR'
      ))
    ) {
      this.props.messageClickedDraftWithFolderBound(folderOfMessage, message);
      this.props.newMessage('');
    }
    if (!this.props.showPreview) {
      window.dispatchEvent(new CustomEvent('ResetList'));
      this.props.setIsFullMessageContent(true);
      this.props.messageClickedWithFolderBound(folderOfMessage, message);
    } else if (this.props.showPreview &&
      (this.props.selectedMessages.filter(x => x.uid).length === 1 ||
        this.props.selectedMessages.filter(x => x.uid).length === 0)) {
        window.dispatchEvent(new CustomEvent('ResetList'));
      if (this.props.selectedMessages.filter(x => x.uid).length === 1) {
        this.selectedMessageLexon(this.props.selectedMessages[0], false);
      }
      this.selectedMessageLexon(message, true);
      this.props.messageClickedWithFolder(folderOfMessage, message);
    }
  }
}

MessageList.propTypes = {
  className: PropTypes.string,
  selectedMessages: PropTypes.array
};

MessageList.defaultProps = {
  className: '',
  selectedMessages: []
};

const mapStateToProps = state => ({
  credentials: getCredentials(state),
  //The selectedFolder is and object with the folder id and the folder name from state.folders.explodedItems[state.application.selectedFolderId]
  selectedFolder: getSelectedFolder(state) || {},
  activeRequests: state.messages.activeRequests,
  messages: getSelectedFolderMessageList(state),
  filtersApplied: state.application.filtersApplied,
  filteredMessages: state.application.filteredMessages,
  searchTotalHits: state.application.searchTotalHits,
  searchFrom: state.application.searchFrom,
  searchSize: state.application.searchSize,
  selectedMessage: state.application.selectedMessage,
  selectedMessages: state.messages.selected,
  selectedMessagesClassification: state.messages.selectedMessages,
  downloadedMessages: state.application.downloadedMessages,
  sign: state.lexon.sign,
  all: state,
  isInvalidAuth: state.application.isInvalidAuth,
  loadingFilterMessages: state.application.loadingFilterMessages,
  showPreview: state.application.showPreview,
  sidebarLexonDocked: state.messages.sidebarLexonDocked,
  lexon: state.lexon
});

const mapDispatchToProps = dispatch => ({
  dispatch,
  messageClicked: (credentials, downloadedMessages, folder, message) => {
    dispatch(selectMessage(message));
    if (message) {
      readMessage(dispatch, credentials, downloadedMessages, folder, message);
    }
  },
  messageClickedWithFolder: (credentials, downloadedMessages, folder, message) => {
    dispatch(selectFolder(folder));
    dispatch(selectMessage(message));
    if (message) {
      readMessage(dispatch, credentials, downloadedMessages, folder, message);
    }
  },
  messageClickedWithFolderNoSeen: (credentials, downloadedMessages, folder, message) => {
    dispatch(selectFolder(folder));
    dispatch(selectMessage(message));
    if (message) {
      readMessage(dispatch, credentials, downloadedMessages, folder, message, 'blob', false);
    }
  },
  messageClickedDraft: (credentials, downloadedMessages, folder, message) => {
    dispatch(selectMessage(message));
    readMessage(
      dispatch,
      credentials,
      downloadedMessages,
      folder,
      message,
      'base64'
    );
  },
  messageClickedDraftWithFolder: (credentials, downloadedMessages, folder, message) => {
    dispatch(selectMessage(message));
    readMessage(
      dispatch,
      credentials,
      downloadedMessages,
      folder,
      message,
      'base64'
    );
  },
  newMessage: sign => editNewMessage(dispatch, [], sign),
  messageSelected: (messages, selected, folderName, folderId, index) =>
    dispatch(setSelected(messages, selected, folderName, folderId, index)),
  toggleMessageFlagged: (credentials, folder, message) =>
    setMessageFlagged(dispatch, credentials, folder, message, !message.flagged),
  toggleMessageFlaggedForFolder: (credentials, folder, message) =>
    setMessageFlagged(dispatch, credentials, folder, message, !message.flagged),
  setError: (err, msg) => dispatch(setError(err, msg)),
  setIsFullMessageContent: isFullMessageContent => dispatch(setIsFullMessageContent(isFullMessageContent)),
  addMessageClassification: messages =>
    dispatch(addMessageClassification(messages)),
  appendFilteredMessages: (newMessages, from, size) =>
    dispatch(appendFilteredMessages(newMessages, from, size))
});

const mergeProps = (stateProps, dispatchProps, ownProps) =>
  Object.assign({}, stateProps, dispatchProps, ownProps, {
    messageClicked: message =>
      dispatchProps.messageClickedWithFolder(
        stateProps.credentials,
        stateProps.downloadedMessages,
        stateProps.selectedFolder,
        message,
        stateProps.sign,
        stateProps.selectedMessage
      ),
    messageClickedWithFolder: (folder, message) =>
      dispatchProps.messageClickedWithFolder(
        stateProps.credentials,
        stateProps.downloadedMessages,
        folder,
        message,
        stateProps.sign,
        stateProps.selectedMessage
      ),
    messageClickedWithFolderBound: (folder, message) =>
      dispatchProps.messageClickedWithFolder(
        stateProps.credentials,
        stateProps.downloadedMessages,
        folder,
        message,
        stateProps.sign,
        stateProps.selectedMessage
      ),
    messageClickedWithFolderNoSeen: (folder, message) =>
      dispatchProps.messageClickedWithFolderNoSeen(
        stateProps.credentials,
        stateProps.downloadedMessages,
        folder,
        message
      ),
    messageClickedDraft: message =>
      dispatchProps.messageClickedDraftWithFolder(
        stateProps.credentials,
        stateProps.downloadedMessages,
        stateProps.selectedFolder,
        message,
        stateProps.sign,
        stateProps.selectedMessage
      ),
    messageClickedDraftWithFolderBound: (folder, message) =>
      dispatchProps.messageClickedDraftWithFolder(
        stateProps.credentials,
        stateProps.downloadedMessages,
        folder,
        message,
        stateProps.sign,
        stateProps.selectedMessage
      ),
    toggleMessageFlagged: message =>
      dispatchProps.toggleMessageFlaggedForFolder(
        stateProps.credentials,
        stateProps.selectedFolder,
        message
      ),
    appendFilteredMessages: (newMessages, from, size) =>
      dispatchProps.appendFilteredMessages(
        newMessages,
        from,
        size
      )
  });

const ConnectedMessageList = withClassNavigate(connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps
)(MessageList));

// Unconnected class exported for unit tests (PEC-8348)
export { MessageList };
export default ConnectedMessageList;
